(() => {
  const MQ = '(min-width: 750px)';

  class VgLangDropdown extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.button = this.querySelector('.vg-lang__button');
      this.list = this.querySelector('.vg-lang__list');
      this.options = Array.from(this.querySelectorAll('[role="option"]'));
      this.select = this.closest('.language-selector')?.querySelector('select.localization-form__select');

      if (!this.button || !this.list || !this.select) return;

      this.button.addEventListener('click', this.#onButtonClick);
      this.button.addEventListener('keydown', this.#onButtonKeyDown);
      this.list.addEventListener('click', this.#onListClick);
      this.list.addEventListener('keydown', this.#onListKeyDown);
      window.matchMedia(MQ).addEventListener('change', () => this.#syncNative());
      this.#syncNative();
    }

    get open() {
      return this.button?.getAttribute('aria-expanded') === 'true';
    }

    #isDesktop() {
      return window.matchMedia(MQ).matches;
    }

    #syncNative() {
      if (!this.select) return;
      if (this.#isDesktop()) {
        this.select.setAttribute('tabindex', '-1');
        this.select.setAttribute('aria-hidden', 'true');
      } else {
        this.select.removeAttribute('tabindex');
        this.select.removeAttribute('aria-hidden');
        this.close(false);
      }
    }

    #onButtonClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.#isDesktop()) return;
      this.open ? this.close() : this.show();
    };

    #onButtonKeyDown = (event) => {
      if (!this.#isDesktop()) return;
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.show();
      }
    };

    #onListClick = (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option) return;
      event.preventDefault();
      this.#choose(option);
    };

    #onListKeyDown = (event) => {
      const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Escape', 'Tab'];
      if (!keys.includes(event.key)) return;

      if (event.key === 'Tab') {
        this.close();
        return;
      }

      event.preventDefault();
      const current = document.activeElement;
      const index = this.options.indexOf(current);

      if (event.key === 'Escape') {
        this.close();
        this.button.focus();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        if (current && this.options.includes(current)) this.#choose(current);
        return;
      }
      if (event.key === 'Home') {
        this.options[0]?.focus();
        return;
      }
      if (event.key === 'End') {
        this.options[this.options.length - 1]?.focus();
        return;
      }
      if (event.key === 'ArrowDown') {
        (this.options[index + 1] || this.options[0])?.focus();
        return;
      }
      if (event.key === 'ArrowUp') {
        (this.options[index - 1] || this.options[this.options.length - 1])?.focus();
      }
    };

    #onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    #onDocumentKey = (event) => {
      if (event.key === 'Escape' && this.open) {
        event.stopPropagation();
        this.close();
        this.button.focus();
      }
    };

    show() {
      if (!this.#isDesktop() || this.open) return;
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      const selected = this.options.find((opt) => opt.getAttribute('aria-selected') === 'true') || this.options[0];
      selected?.focus();
      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('keydown', this.#onDocumentKey);
    }

    close(restoreFocus = false) {
      if (!this.open) return;
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('keydown', this.#onDocumentKey);
      if (restoreFocus) this.button.focus();
    }

    #choose(option) {
      const value = option.getAttribute('data-value');
      if (!value || !this.select) return;

      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (!customElements.get('vg-lang-dropdown')) {
    customElements.define('vg-lang-dropdown', VgLangDropdown);
  }
})();
